import {
  FloatingFocusManager,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import { cx } from "@jugl-web/utils";
import { FC, ReactNode, RefObject } from "react";

export type ChatDrawerPlacement = "left" | "right";

export interface ChatDrawerProps {
  isOpen: boolean;
  children: ReactNode;
  placement: ChatDrawerPlacement;
  positionStrategy?: "fixed" | "absolute";
  initialFocusRef?: RefObject<HTMLElement | null>;
  panelClassName?: string;
  onClose: () => void;
}

export const ChatDrawer: FC<ChatDrawerProps> = ({
  isOpen,
  children,
  placement,
  positionStrategy = "absolute",
  initialFocusRef,
  panelClassName,
  onClose,
}) => {
  const { refs, context } = useFloating({
    strategy: positionStrategy,
    open: isOpen,
    onOpenChange: (nextOpen) => {
      if (!nextOpen) {
        onClose();
      }
    },
  });

  const dismiss = useDismiss(context, {
    outsidePress: true,
    escapeKey: true,
  });

  const role = useRole(context, {
    role: "dialog",
  });

  const { getFloatingProps } = useInteractions([dismiss, role]);

  const { isMounted, styles: backdropStyles } = useTransitionStyles(context, {
    duration: 200,
    initial: { opacity: 0 },
    open: { opacity: 1 },
    close: { opacity: 0 },
  });

  const slideInitial =
    placement === "left" ? "translateX(-100%)" : "translateX(100%)";

  const { styles: panelTransitionStyles } = useTransitionStyles(context, {
    duration: 200,
    initial: { transform: slideInitial },
    open: { transform: "translateX(0%)" },
    close: { transform: slideInitial },
  });

  if (!isMounted) {
    return null;
  }

  const positionClass = positionStrategy === "fixed" ? "fixed" : "absolute";

  return (
    <div className={cx(positionClass, "inset-0 z-[100] overflow-hidden")}>
      <FloatingFocusManager
        context={context}
        modal
        guards
        initialFocus={initialFocusRef ?? refs.floating}
      >
        <div
          ref={refs.setFloating}
          className={cx(positionClass, "inset-0 z-10")}
          {...getFloatingProps()}
        >
          {/* Backdrop overlay */}
          <div
            aria-hidden
            className={cx(
              positionClass,
              "inset-0 bg-[#00000050] backdrop-blur-[3px]"
            )}
            style={backdropStyles}
            onClick={onClose}
          />

          {/* Slide-over panel */}
          <div
            style={panelTransitionStyles}
            className={cx(
              "absolute inset-y-0 z-10 overflow-hidden bg-white outline-none",
              placement === "left" ? "left-0" : "right-0",
              panelClassName
            )}
          >
            {children}
          </div>
        </div>
      </FloatingFocusManager>
    </div>
  );
};
